import { ERROR_MESSAGES } from "../../../shared/errors";
import { SCHEMA_BLOCK_HEADER_REGEX, VALID_ENUM_VALUE_REGEX } from "../../regex";
import { EnvxError, EnvxVarSchema, ParsedLine } from "../../types";

function isSafeString(str: string): boolean {
  return VALID_ENUM_VALUE_REGEX.test(str);
}

function isSchemaHeader(line: string): boolean {
  return SCHEMA_BLOCK_HEADER_REGEX.test(line);
}

const assertNoTrailingPunctuation = (raw: string, lineNumber: number) => {
  const forbiddenChars = [
    ",",
    ";",
    ":",
    ".",
    "-",
    "?",
    "!",
    "|",
    "\\",
    "/",
    "(",
    ")",
    "{",
    "<",
    ">",
    "`",
    "~",
    "=",
  ];

  const lastChar = raw.trimEnd().slice(-1);

  if (forbiddenChars.includes(lastChar)) {
    throw new EnvxError(
      `[envx:error] Line ${
        lineNumber + 1
      }: Value ends with a suspicious character (${lastChar}). Please remove unnecessary character  at the end.`
    );
  }
};

function applyDefault(
  value: string,
  lineNumber: number,
  target: EnvxVarSchema
) {
  const { type, values } = target;
  if (!type) {
    // [THROW_ERR_MSG] => Throws error if "type" is missing before "default".
    throw new EnvxError(
      ERROR_MESSAGES.lib.parser.typeRequiredBeforeDefault(lineNumber)
    );
  }

  switch (type) {
    case "number":
      const num = Number(value);
      if (isNaN(num)) {
        throw new EnvxError(
          ERROR_MESSAGES.lib.parser.defaultMustBeNumber(lineNumber)
        );
      }
      target.default = num;
      break;

    case "boolean":
      if (value !== "true" && value !== "false") {
        throw new EnvxError(
          ERROR_MESSAGES.lib.parser.defaultMustBeBoolean(lineNumber)
        );
      }
      target.default = value === "true";
      break;

    case "enum":
      if (!values || !values.includes(value)) {
        throw new EnvxError(
          ERROR_MESSAGES.lib.parser.defaultNotInEnum(lineNumber, value)
        );
      }
      target.default = value;
      break;

    default:
      target.default = value;
  }
}

function splitKeyValue(line: string, lineNumber: number): [string, string] {
  const eqIndex = line.indexOf("=");
  if (eqIndex === -1) {
    // * Splits a line into key and value by first '=' character.
    // * Throws error if '=' not found.
    throw new EnvxError(ERROR_MESSAGES.lib.parser.missingDelimiter(lineNumber));
  }
  return [line.slice(0, eqIndex), line.slice(eqIndex + 1)];
}

function stripQuotes(value: string): string {
  const first = value[0];
  const last = value[value.length - 1];
  if ((first === `"` && last === `"`) || (first === `'` && last === `'`)) {
    return value.slice(1, -1);
  }
  return value;
}

/**
 * Finds index of unquoted '#' symbol to identify inline comment start.
 */
function findCommentIndex(line: string): number {
  let inSingleQuote = false;
  let inDoubleQuote = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"' && !inSingleQuote) {
      inDoubleQuote = !inDoubleQuote;
    } else if (char === "'" && !inDoubleQuote) {
      inSingleQuote = !inSingleQuote;
    } else if (char === "#" && !inSingleQuote && !inDoubleQuote) {
      return i;
    }
  }
  return -1; // if comment dont have return `-1`
}

// func(handleMultilineLine) => its purpose is to handle multi-line strings
function handleMultilineLine(
  line: string,
  multilineKey: string,
  multilineBuffer: string[],
  env: ParsedLine[]
): { multilineKey: string | null; multilineBuffer: string[] } {
  const trimmedLine = line.trim();
  if (trimmedLine.includes('"""')) {
    const quoteIndex = trimmedLine.indexOf('"""');
    const beforeQuote = trimmedLine.slice(0, quoteIndex);
    multilineBuffer.push(beforeQuote.trimEnd());
    env.push({ key: multilineKey, value: multilineBuffer.join("\n") });
    return { multilineKey: null, multilineBuffer: [] };
  } else {
    multilineBuffer.push(line);
    return { multilineKey, multilineBuffer };
  }
}

export {
  isSafeString,
  isSchemaHeader,
  assertNoTrailingPunctuation,
  applyDefault,
  splitKeyValue,
  stripQuotes,
  findCommentIndex,
  handleMultilineLine,
};
